import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as data from './mock-data';

const LIST_FALLBACK: Record<string, unknown> = { data: [], count: 0 };

function periodParam(url: string): string {
  const match = url.match(/[?&]period=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function contractIdParam(url: string): string {
  const match = url.match(/[?&]contractId=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function yearParam(url: string): number {
  const match = url.match(/[?&]year=([^&]+)/);
  const year = match ? Number(decodeURIComponent(match[1])) : NaN;
  return Number.isInteger(year) && year > 0 ? year : new Date().getFullYear();
}

function bodyPeriod(body: unknown): string {
  if (body && typeof body === 'object' && 'period' in body) {
    return String((body as { period: unknown }).period ?? '');
  }
  return '';
}

interface Route {
  method: string;
  pattern: RegExp;
  body: (match: RegExpMatchArray, req: HttpRequest<unknown>) => unknown;
}

const routes: Route[] = [
  { method: 'GET', pattern: /\/hr\/companies(\b|\?|$)/, body: () => data.mockAssignedCompanies },
  { method: 'GET', pattern: /\/hr\/summary(\b|\?|$)/, body: () => data.mockSummary },
  { method: 'GET', pattern: /\/hr\/payroll\/pit11\.xml/, body: () => new Blob([data.mockPit11Xml()], { type: 'application/xml' }) },
  { method: 'GET', pattern: /\/hr\/payroll\/kedu\.xml/, body: () => new Blob([data.mockKeduXml()], { type: 'application/xml' }) },
  { method: 'GET', pattern: /\/hr\/payroll\/pit4r\.xml/, body: (_m, req) => new Blob([data.mockPit4rXml(yearParam(req.url))], { type: 'application/xml' }) },
  { method: 'GET', pattern: /\/hr\/payroll\/pit11-batch\.zip/, body: () => data.mockPit11BatchZip() },
  { method: 'GET', pattern: /\/hr\/payroll\/payslip/, body: (_m, req) => new Blob([data.mockPayslipHtml(periodParam(req.url))], { type: 'text/html' }) },
  { method: 'GET', pattern: /\/hr\/payroll\/transfers\.csv/, body: (_m, req) => new Blob(['﻿' + data.mockTransfersCsv(periodParam(req.url))], { type: 'text/csv' }) },
  { method: 'GET', pattern: /\/hr\/payroll\/period/, body: (_m, req) => data.mockPayrollPeriod(periodParam(req.url)) },
  { method: 'POST', pattern: /\/hr\/payroll\/lock/, body: (_m, req) => ({ ...data.mockPayrollPeriod(bodyPeriod(req.body)), status: 'locked', lockedBy: 'Agnieszka Kadrowa', lockedAt: new Date().toISOString() }) },
  { method: 'POST', pattern: /\/hr\/payroll\/pay/, body: (_m, req) => ({ ...data.mockPayrollPeriod(bodyPeriod(req.body)), status: 'paid', paidAt: new Date().toISOString() }) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/documents\/([^/]+)\/download/, body: () => new Blob(['%PDF-1.4 mock document'], { type: 'application/pdf' }) },
  { method: 'POST', pattern: /\/hr\/employees\/([^/]+)\/contracts\/([^/]+)\/terminate(\b|\?|$)/, body: (m, req) => data.mockTerminateContract(m[1], m[2], req.body) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/contracts\/([^/]+)\/document(\b|\?|$)/, body: (m) => new Blob([data.mockContractDocumentHtml(m[1], m[2])], { type: 'text/html' }) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/certificate(\b|\?|$)/, body: (m, req) => new Blob([data.mockCertificateHtml(m[1], contractIdParam(req.url))], { type: 'text/html' }) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/contracts(\b|\?|$)/, body: (m) => data.mockContractsByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/absences(\b|\?|$)/, body: (m) => data.mockAbsencesByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/trips(\b|\?|$)/, body: (m) => data.mockTripsByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/documents(\b|\?|$)/, body: (m) => data.mockDocumentsByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/time-entries(\b|\?|$)/, body: (m) => data.mockTimeEntriesByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)\/b2b-invoices(\b|\?|$)/, body: (m) => data.mockB2BInvoicesByEmployee(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees\/([^/]+)$/, body: (m) => data.mockEmployeeById(m[1]) },
  { method: 'GET', pattern: /\/hr\/employees(\b|\?|$)/, body: () => data.mockEmployeeList },
  { method: 'GET', pattern: /\/hr\/payroll(\b|\?|$)/, body: () => data.mockPayroll },
  { method: 'GET', pattern: /\/hr\/payer-settings(\b|\?|$)/, body: () => data.mockPayerSettings },
  { method: 'GET', pattern: /\/hr\/tax-settlements(\b|\?|$)/, body: () => data.mockTaxSettlements },
  { method: 'GET', pattern: /\/hr\/pending-approvals(\b|\?|$)/, body: () => data.mockPendingApprovals },
  { method: 'GET', pattern: /\/hr\/pending-trips(\b|\?|$)/, body: () => data.mockPendingTrips },
  { method: 'POST', pattern: /\/hr\/employees\/([^/]+)\/trips\/([^/]+)\/reject(\b|\?|$)/, body: (m) => data.mockRejectTrip(m[1], m[2]) },
  { method: 'POST', pattern: /\/hr\/employees\/([^/]+)\/trips\/([^/]+)\/settle(\b|\?|$)/, body: (m, req) => data.mockSettleTrip(m[1], m[2], req.body) },
  { method: 'GET', pattern: /\/support\/tickets\/([^/]+)(\b|\?|$)/, body: (m) => data.mockTicketDetail(m[1]) },
  { method: 'GET', pattern: /\/support\/tickets(\b|\?|$)/, body: () => data.mockSupportTickets },
];

@Injectable()
export class MockBackendInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isMockTarget(req.url)) {
      return next.handle(req);
    }

    if (this.isKeycloak(req.url)) {
      return this.respond(req, this.keycloakBody(req.url));
    }

    const path = this.pathOf(req.url);
    for (const route of routes) {
      if (route.method !== req.method) continue;
      const match = path.match(route.pattern);
      if (match) {
        return this.respond(req, route.body(match, req));
      }
    }

    return this.respond(req, this.fallbackBody(req));
  }

  private isMockTarget(url: string): boolean {
    return url.startsWith(environment.apiUrl) || this.isKeycloak(url);
  }

  private isKeycloak(url: string): boolean {
    return url.startsWith(environment.keycloak.url) || url.includes('/realms/');
  }

  private keycloakBody(url: string): unknown {
    if (url.includes('/logout')) return {};
    return data.mockKeycloakToken();
  }

  private pathOf(url: string): string {
    return url.startsWith(environment.apiUrl) ? url.slice(environment.apiUrl.length) : url;
  }

  private fallbackBody(req: HttpRequest<unknown>): unknown {
    if (req.responseType === 'blob') return new Blob([], { type: 'application/octet-stream' });
    if (req.method === 'DELETE' || req.method === 'PATCH' || req.method === 'PUT') return {};
    if (req.method === 'POST' && !/\/list$|\/search$/.test(this.pathOf(req.url))) {
      return { id: 'mock-' + Math.random().toString(36).slice(2, 10), ok: true };
    }
    return LIST_FALLBACK;
  }

  private respond(req: HttpRequest<unknown>, body: unknown): Observable<HttpEvent<unknown>> {
    const responseBody = req.responseType === 'blob' && !(body instanceof Blob)
      ? new Blob([JSON.stringify(body)], { type: 'application/json' })
      : body;
    return of(new HttpResponse({ status: 200, body: responseBody, url: req.url })).pipe(delay(80));
  }
}
